/**
 * Operations-tab TanStack Query hooks (Plan 08 Task 8.2).
 *
 * One hook per endpoint, each unwrapping the server envelope and returning
 * the inner payload. Query keys start with `['operations', <endpoint>,
 * …, filtersQuestion(filters)]` so the cache is keyed by the semantic filter
 * set (not the raw URL).
 *
 * `staleTime: 60_000` + `placeholderData: keepPreviousData` — and since the
 * recount rule, the second of those no longer means the previous filter set's
 * answer stays on screen. `useFilteredQuery` reports that window as "nothing
 * counted yet" (hooks/use-filtered-query.ts), so the tab blanks and says so.
 * What `keepPreviousData` still buys: telling a recount apart from a first
 * load, and an instant switch back to a filter set already in the cache.
 *
 * Tool-usage params (`limit`), skills-usage params (`source`, `limit`), and
 * error-breakdown params (`limit`) participate in the query key so param
 * changes issue a refetch.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { useFilteredQuery } from '@/hooks/use-filtered-query';

import { filtersQuestion, type Filters } from '@/lib/filters-schema';
import {
  getComplexityDistribution,
  getCostTrend,
  getErrorBreakdown,
  getErrorBucketTraces,
  getJiraMetrics,
  getLatencyDistribution,
  getOperationsKpis,
  getSkillDetail,
  getSkillsUsage,
  getToolUsage,
  type ErrorBreakdownParams,
  type ErrorBucketTracesParams,
  type ToolUsageParams,
} from '@/services/operations-api';
import type {
  ComplexityRow,
  CostTrendPoint,
  ErrorBreakdownRow,
  ErrorBucketTraceRow,
  JiraMetrics,
  LatencyDistributionData,
  OperationsKpis,
  SkillDetail,
  SkillUsageRow,
  ToolUsageRow,
} from '@/services/api-types';

const STALE_TIME = 60_000;

export function useOperationsKpis(filters: Filters) {
  return useFilteredQuery<OperationsKpis>({
    queryKey: ['operations', 'kpis', filtersQuestion(filters)],
    queryFn: async () => (await getOperationsKpis(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useCostTrend(
  filters: Filters,
  granularity: Filters['granularity'],
) {
  return useFilteredQuery<CostTrendPoint[]>({
    queryKey: [
      'v2',
      'operations',
      'cost-trend',
      granularity,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getCostTrend(filters, granularity)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useLatencyDistribution(filters: Filters) {
  return useFilteredQuery<LatencyDistributionData>({
    queryKey: [
      'v2',
      'operations',
      'latency-distribution',
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getLatencyDistribution(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useErrorBreakdown(
  filters: Filters,
  params: ErrorBreakdownParams = {},
) {
  return useFilteredQuery<ErrorBreakdownRow[]>({
    queryKey: [
      'v2',
      'operations',
      'error-breakdown',
      params.limit ?? null,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getErrorBreakdown(filters, params)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Error-bucket drill-down — lazy, only fires when `bucket` is set (user
 * opened the modal). `enabled` mirrors `useFeedbackTraceDetail` — zero
 * traffic until the dialog is on-screen.
 */
export function useErrorBucketTraces(
  bucket: string | null,
  filters: Filters,
  params: ErrorBucketTracesParams = {},
) {
  return useFilteredQuery<ErrorBucketTraceRow[]>({
    queryKey: [
      'v2',
      'operations',
      'error-bucket-traces',
      bucket,
      params.limit ?? null,
      filtersQuestion(filters),
    ],
    queryFn: async () =>
      (await getErrorBucketTraces(bucket as string, filters, params)).data,
    enabled: Boolean(bucket),
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

export function useToolUsage(filters: Filters, params: ToolUsageParams = {}) {
  return useFilteredQuery<ToolUsageRow[]>({
    queryKey: [
      'v2',
      'operations',
      'tools',
      params.limit ?? null,
      filtersQuestion(filters),
    ],
    queryFn: async () => (await getToolUsage(filters, params)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Skills usage — Phase 9: source/limit args removed. Returns ALL preloaded
 * skills, no top-N. Single-arg signature; query key keyed only on
 * filtersQuestion(filters).
 */
export function useSkillsUsage(filters: Filters) {
  return useFilteredQuery<SkillUsageRow[]>({
    queryKey: ['operations', 'skills', filtersQuestion(filters)],
    queryFn: async () => (await getSkillsUsage(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/** Skill detail (markdown content + Confluence article URLs) for the
 *  SkillDetailModal. Lazy: `enabled: topicId !== null` so the query
 *  doesn't fire until the modal opens. */
export function useSkillDetail(topicId: string | null) {
  return useQuery<SkillDetail>({
    queryKey: ['operations', 'skill-detail', topicId],
    queryFn: async () => {
      if (!topicId) throw new Error('topicId required');
      return (await getSkillDetail(topicId)).data;
    },
    enabled: topicId !== null,
    staleTime: STALE_TIME,
  });
}

export function useComplexityDistribution(filters: Filters) {
  return useFilteredQuery<ComplexityRow[]>({
    queryKey: ['operations', 'complexity', filtersQuestion(filters)],
    queryFn: async () => (await getComplexityDistribution(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}

/**
 * Jira prefill metrics — matches Slack `/report` Jira numbers. Single
 * lightweight query (three scalars), same `staleTime` as the rest of the
 * Operations tab so it participates in the shared refetch cadence.
 */
export function useJiraMetrics(filters: Filters) {
  return useFilteredQuery<JiraMetrics>({
    queryKey: ['operations', 'jira-metrics', filtersQuestion(filters)],
    queryFn: async () => (await getJiraMetrics(filters)).data,
    staleTime: STALE_TIME,
    placeholderData: keepPreviousData,
  });
}
